import { useState, useEffect, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSafeBack } from "../hooks/useSafeBack";
import { useTruthState } from "../hooks/useTruthState";
import { TruthReceipt } from "../components/TruthReceipt";
import { Button } from "../components/ui/button";
import { UpgradePrompt } from "../components/UpgradePrompt";
// B0 Step 5 — Merge C1 + B2 into B1. The standalone `PostLessonReflection`
// modal was a learner-side duplicate of the Goal Outcome Loop surface and
// the Confidence Check route. Both have been folded into the calm
// `LessonEndReflection` inline rail below; the modal component remains in
// the codebase for historical reference only and is no longer mounted.
import LessonEndReflection from "../components/learner/LessonEndReflection";
import { lessonNarrationText } from "../utils/lessonNarrationText";
import BrowserNarration from "../components/BrowserNarration";
import OfflineDownloadButton from "../components/OfflineDownloadButton";
import { getLessonDownload, touchLessonDownload } from "../lib/offlineCache";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import axios from "axios";
import {
  ArrowLeft,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Clock,
  BookOpen,
  Play,
  FileText,
  Image,
  Headphones,
  Settings,
  List,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Lesson() {
  const { lessonId } = useParams();
  const { user, hasActiveSubscription } = useAuth();
  const navigate = useNavigate();
  const goBack = useSafeBack();
  const [lesson, setLesson] = useState(null);
  const [module, setModule] = useState(null);
  const [allLessons, setAllLessons] = useState([]);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [markingComplete, setMarkingComplete] = useState(false);
  const [activeTab, setActiveTab] = useState("written");
  const [hasAccess, setHasAccess] = useState(true);
  const [lessonsRemaining, setLessonsRemaining] = useState(null);
  const [hasReflected, setHasReflected] = useState(false);
  // Batch P1 · Lesson Truth-State — single canonical async
  // receipt for every Lesson-domain action (load, mark complete,
  // mark incomplete). Bespoke `toast.success` / `toast.error`
  // calls have been removed in favour of <TruthReceipt />.
  const truth = useTruthState();

  const isSubscribed = hasActiveSubscription() || user?.role === "admin";

  // Get user preferences
  const prefs = user?.learning_preferences || {
    preferred_style: "step_by_step",
    show_written_content: true,
    show_visual_content: true,
    show_video_content: true,
    show_audio_content: true,
    calm_mode: false,
    large_text: false,
  };

  useEffect(() => {
    // Set initial tab based on user preferences and available content
    if (lesson) {
      const stylePriority = {
        step_by_step: ["written", "visual", "video", "audio"],
        visual: ["visual", "video", "written", "audio"],
        audio: ["audio", "video", "written", "visual"],
        repetition: ["written", "visual", "audio", "video"],
      };
      
      const priority = stylePriority[prefs.preferred_style] || stylePriority.step_by_step;
      const contentAvailable = getAvailableContent();
      
      for (const type of priority) {
        if (contentAvailable.includes(type)) {
          setActiveTab(type);
          break;
        }
      }
    }
  }, [lesson, prefs.preferred_style]);

  const fetchLessonData = useCallback(async () => {
    setLoading(true);
    try {
      await truth.run(
        async () => {
          const requests = [
            axios.get(`${API_URL}/api/lessons/${lessonId}`, { withCredentials: true }),
            axios.get(`${API_URL}/api/lessons`, { withCredentials: true }),
            axios.get(`${API_URL}/api/modules`, { withCredentials: true }),
            axios.get(`${API_URL}/api/progress`, { withCredentials: true }),
          ];
          // Check access for non-subscribed users
          if (!isSubscribed) {
            requests.push(
              axios.get(`${API_URL}/api/lessons/${lessonId}/access`, { withCredentials: true }).catch(() => ({ data: { has_access: false } }))
            );
          }
          const results = await Promise.all(requests);

          setLesson(results[0].data);
          setAllLessons(results[1].data);
          setProgress(results[3].data);
          const lessonModule = results[2].data.find((m) => m.id === results[0].data.module_id);
          setModule(lessonModule);

          // Auto-mark "in_progress" if the learner hasn't completed it yet. Silent
          // on failure — completion path still writes through /api/progress.
          try {
            const existing = results[3].data.find((p) => p.lesson_id === lessonId);
            if (!existing || (!existing.completed && existing.status !== "in_progress")) {
              axios.post(
                `${API_URL}/api/progress`,
                { lesson_id: lessonId, completed: false, status: "in_progress" },
                { withCredentials: true },
              ).catch(() => { /* silent */ });
            }
          } catch { /* silent */ }

          if (!isSubscribed && results[4]) {
            const access = results[4].data;
            setHasAccess(access.has_access);
            if (access.lessons_remaining !== undefined) {
              setLessonsRemaining(access.lessons_remaining);
            }
          }
          // Check reflection status
          try {
            const refRes = await axios.get(`${API_URL}/api/reflection/${lessonId}`, { withCredentials: true });
            if (refRes.data.reflection) setHasReflected(true);
          } catch {}
          return results[0].data;
        },
        { failedMessage: "Couldn't load the lesson." },
      );
    } catch (error) {
      // truth.run already recorded the canonical `failed` state.
      // Nothing else to do — the TruthReceipt surfaces the error
      // with Retry / Dismiss.
      console.error("Error fetching lesson:", error);
    } finally {
      setLoading(false);
    }
  }, [lessonId, isSubscribed, truth]);

  useEffect(() => {
    fetchLessonData();
  }, [fetchLessonData]);

  const isLessonCompleted = (id) => {
    return progress.some((p) => p.lesson_id === id && p.completed);
  };

  // Fire offline_opened metric once per lesson when the user is offline OR
  // already has a downloaded copy — proves the value of the feature.
  useEffect(() => {
    if (!lesson) return;
    let cancelled = false;
    (async () => {
      const isOffline = typeof navigator !== "undefined" && !navigator.onLine;
      const saved = await getLessonDownload(lessonId);
      if (cancelled) return;
      if (saved) {
        // Fire-and-forget — update last-opened for shelf sort order.
        touchLessonDownload(lessonId);
      }
      if (isOffline || saved) {
        axios.post(`${API_URL}/api/metrics/event`, {
          event: "offline_opened",
          role: "learner",
          testid: "offline-download",
          metadata: {
            lesson_id: lessonId,
            while_offline: isOffline,
            was_downloaded: !!saved,
          },
        }, { withCredentials: true }).catch(() => {});
      }
    })();
    return () => { cancelled = true; };
  }, [lesson, lessonId]);

  const handleMarkComplete = async () => {
    setMarkingComplete(true);
    try {
      await truth.run(
        async () => {
          await axios.post(
            `${API_URL}/api/progress`,
            { lesson_id: lessonId, completed: true },
            { withCredentials: true }
          );
          setProgress([...progress, { lesson_id: lessonId, completed: true }]);
        },
        {
          completedMessage: "Nice one — that's ticked off",
          failedMessage:    "Couldn't mark this lesson complete.",
        },
      );
    } catch (error) {
      // truth.run captured the failure; TruthReceipt shows Retry.
      console.error("Error marking complete:", error);
    } finally {
      setMarkingComplete(false);
    }
  };

  const handleMarkIncomplete = async () => {
    setMarkingComplete(true);
    try {
      await truth.run(
        async () => {
          await axios.post(
            `${API_URL}/api/progress`,
            { lesson_id: lessonId, completed: false },
            { withCredentials: true }
          );
          setProgress(progress.filter((p) => p.lesson_id !== lessonId));
        },
        {
          completedMessage: "Marked as incomplete.",
          failedMessage:    "Couldn't update the lesson status.",
        },
      );
    } catch (error) {
      console.error("Error marking incomplete:", error);
    } finally {
      setMarkingComplete(false);
    }
  };

  // Batch P1 · Lesson Truth-State — Home-style calm auto-clear.
  // `completed` states are terminal-successes; they should whisper
  // (~1200ms) then clear. `failed` states stay visible until the
  // user retries or dismisses (Truth Doctrine — never a dead-end).
  useEffect(() => {
    if (truth.state === "completed") {
      const t = setTimeout(() => truth.setState(null), 1200);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [truth]);

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    if (url.includes("/embed/")) return url;
    const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([^&?]+)/);
    if (match) return `https://www.youtube.com/embed/${match[1]}`;
    return url;
  };

  const getAvailableContent = () => {
    if (!lesson) return [];
    const available = [];
    if (lesson.written_content || lesson.description) available.push("written");
    if (lesson.visual_content?.length > 0) available.push("visual");
    if (lesson.video_url) available.push("video");
    // Offer Karen narration; legacy recordings require a transcript before narration
    if (lesson.written_content || lesson.audio_url) available.push("audio");
    return available;
  };

  // Filter content based on user preferences
  const getVisibleContent = () => {
    const available = getAvailableContent();
    return available.filter((type) => {
      switch (type) {
        case "written": return prefs.show_written_content;
        case "visual": return prefs.show_visual_content;
        case "video": return prefs.show_video_content;
        case "audio": return prefs.show_audio_content;
        default: return true;
      }
    });
  };

  const currentIndex = allLessons.findIndex((l) => l.id === lessonId);
  const prevLesson = currentIndex > 0 ? allLessons[currentIndex - 1] : null;
  const nextLesson = currentIndex < allLessons.length - 1 ? allLessons[currentIndex + 1] : null;

  // Check if this is the last lesson in the current module
  const moduleLessons = allLessons.filter((l) => l.module_id === lesson?.module_id);
  const isLastInModule = lesson && moduleLessons.length > 0 &&
    moduleLessons[moduleLessons.length - 1]?.id === lessonId;

  if (loading) {
    return (
      <div className="min-h-dvh no-overflow-x bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700"></div>
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="min-h-dvh no-overflow-x bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-500 mb-4">We couldn't find that lesson</p>
          <Link to="/dashboard">
            <Button>Back to Dashboard</Button>
          </Link>
        </div>
      </div>
    );
  }

  const isCompleted = isLessonCompleted(lessonId);
  const visibleContent = getVisibleContent();
  const embedUrl = getYouTubeEmbedUrl(lesson.video_url);
  const textSizeClass = prefs.large_text ? "text-lg" : "text-base";

  return (
    <div className={`min-h-dvh no-overflow-x bg-slate-50 ${prefs.calm_mode ? "calm-mode" : ""}`}>
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <button data-testid="lesson-back" onClick={() => goBack()} className="text-slate-600 hover:text-slate-900">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-slate-500 uppercase tracking-wide font-medium">
              {module?.title}
            </p>
            <h1 className="font-bold text-slate-900 truncate">{lesson.title}</h1>
          </div>
          <Link to="/learn/styles" className="text-slate-400 hover:text-slate-600">
            <Settings className="w-5 h-5" />
          </Link>
        </div>
      </header>

      <div className="max-w-4xl mx-auto">
        {/* Upgrade Wall for locked content */}
        {!hasAccess && (
          <div className="px-4 py-6">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="p-5 border-b border-slate-100">
                <h2 className="font-bold text-slate-900">{lesson.title}</h2>
                {lesson.description && (
                  <p className="text-sm text-slate-500 mt-1 line-clamp-2">{lesson.description}</p>
                )}
              </div>
              <UpgradePrompt variant="wall" />
            </div>
          </div>
        )}

        {/* Content Tabs - only show if has access */}
        {hasAccess && visibleContent.length > 1 && (
          <div className="bg-white border-b border-slate-200 px-4">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="h-12 bg-transparent gap-2 w-full justify-start">
                {visibleContent.includes("written") && (
                  <TabsTrigger
                    value="written"
                    data-testid="tab-written"
                    className="flex items-center gap-2 data-[state=active]:bg-blue-100 data-[state=active]:text-blue-700"
                  >
                    <FileText className="w-4 h-4" />
                    <span className="hidden sm:inline">Read</span>
                  </TabsTrigger>
                )}
                {visibleContent.includes("visual") && (
                  <TabsTrigger
                    value="visual"
                    data-testid="tab-visual"
                    className="flex items-center gap-2 data-[state=active]:bg-blue-100 data-[state=active]:text-blue-700"
                  >
                    <Image className="w-4 h-4" />
                    <span className="hidden sm:inline">Visual</span>
                  </TabsTrigger>
                )}
                {visibleContent.includes("video") && (
                  <TabsTrigger
                    value="video"
                    data-testid="tab-video"
                    className="flex items-center gap-2 data-[state=active]:bg-blue-100 data-[state=active]:text-blue-700"
                  >
                    <Play className="w-4 h-4" />
                    <span className="hidden sm:inline">Watch</span>
                  </TabsTrigger>
                )}
                {visibleContent.includes("audio") && (
                  <TabsTrigger
                    value="audio"
                    data-testid="tab-audio"
                    className="flex items-center gap-2 data-[state=active]:bg-blue-100 data-[state=active]:text-blue-700"
                  >
                    <Headphones className="w-4 h-4" />
                    <span className="hidden sm:inline">Listen</span>
                  </TabsTrigger>
                )}
              </TabsList>
            </Tabs>
          </div>
        )}

        {/* Content Area */}
        {hasAccess && (
        <div className="px-4 py-6">
          {/* Free tier notice */}
          {lessonsRemaining !== null && lessonsRemaining >= 0 && (
            <div data-testid="free-tier-notice" className="bg-blue-50 border border-blue-200 rounded-2xl p-4 mb-6 flex items-center gap-3">
              <div className="flex-1">
                <p className="text-sm text-blue-800 font-medium">
                  {lessonsRemaining > 0
                    ? `You've got ${lessonsRemaining} free lesson${lessonsRemaining !== 1 ? "s" : ""} left`
                    : "This is your last free one"}
                </p>
              </div>
            </div>
          )}

          {/* Download for Offline — user-facing intentional save */}
          <div className="mb-6">
            <OfflineDownloadButton lessonId={lessonId} />
          </div>

          {/* Written Content */}
          {activeTab === "written" && (
            <div data-testid="content-written" className="space-y-6">
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <h2 className={`font-bold text-xl text-slate-900 mb-4 ${prefs.large_text ? "text-2xl" : ""}`}>
                  {lesson.title}
                </h2>
                
                {/* Key Points (for repetition learners) */}
                {lesson.key_points?.length > 0 && (
                  <div className="bg-blue-50 rounded-xl p-4 mb-6">
                    <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                      <List className="w-4 h-4" />
                      Key Points
                    </h3>
                    <ul className={`space-y-2 ${textSizeClass}`}>
                      {lesson.key_points.map((point, i) => (
                        <li key={i} className="flex items-start gap-2 text-blue-800">
                          <span className="w-5 h-5 bg-blue-200 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold mt-0.5">
                            {i + 1}
                          </span>
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Main Content */}
                <div className={`prose prose-slate max-w-none ${textSizeClass} ${prefs.large_text ? "prose-lg" : ""}`}>
                  {prefs.calm_mode && lesson.calm_mode_content ? (
                    <div className="whitespace-pre-wrap leading-relaxed">
                      {lesson.calm_mode_content}
                    </div>
                  ) : lesson.written_content ? (
                    <div className="leading-relaxed text-slate-700 space-y-4">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          h1: ({node, ...p}) => <h2 className="text-xl font-semibold text-slate-900 mt-6 mb-2" {...p} />,
                          h2: ({node, ...p}) => <h3 className="text-lg font-semibold text-slate-900 mt-5 mb-2" {...p} />,
                          h3: ({node, ...p}) => <h4 className="text-base font-semibold text-slate-900 mt-4 mb-2" {...p} />,
                          p:  ({node, ...p}) => <p className="leading-relaxed" {...p} />,
                          ul: ({node, ...p}) => <ul className="list-disc pl-6 space-y-1" {...p} />,
                          ol: ({node, ...p}) => <ol className="list-decimal pl-6 space-y-1" {...p} />,
                          li: ({node, ...p}) => <li className="leading-relaxed" {...p} />,
                          strong: ({node, ...p}) => <strong className="font-semibold text-slate-900" {...p} />,
                          em: ({node, ...p}) => <em className="italic" {...p} />,
                          blockquote: ({node, ...p}) => (
                            <blockquote className="border-l-4 border-amber-300 bg-amber-50 px-4 py-3 rounded-r my-4 italic text-slate-700" {...p} />
                          ),
                          code: ({node, ...p}) => <code className="bg-slate-100 text-slate-800 px-1.5 py-0.5 rounded text-sm" {...p} />,
                          a: ({node, ...p}) => <a className="text-blue-600 underline" target="_blank" rel="noopener noreferrer" {...p} />,
                        }}
                      >
                        {lesson.written_content}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    <p className="text-slate-600 leading-relaxed">{lesson.description}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Visual Content */}
          {activeTab === "visual" && (
            <div data-testid="content-visual" className="space-y-4">
              {lesson.visual_content?.length > 0 ? (
                lesson.visual_content.map((imageUrl, i) => (
                  <div key={i} className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
                    <img
                      src={imageUrl}
                      alt={`${lesson.title} - Image ${i + 1}`}
                      className="w-full h-auto"
                    />
                  </div>
                ))
              ) : (
                <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
                  <Image className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No pictures for this one yet</p>
                  <p className="text-sm text-slate-400 mt-2">Try one of the other formats for now</p>
                </div>
              )}
            </div>
          )}

          {/* Video Content */}
          {activeTab === "video" && (
            <div data-testid="content-video">
              <div className="bg-slate-900 rounded-2xl overflow-hidden">
                <div className="video-container">
                  {embedUrl ? (
                    <iframe
                      data-testid="lesson-video-player"
                      src={embedUrl}
                      title={lesson.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-slate-400">
                      <div className="text-center">
                        <Play className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>No video for this one yet</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Audio Content — Karen reads the lesson; never silently substitute an unverified recording */}
          {activeTab === "audio" && (
            <div data-testid="content-audio" className="space-y-4">
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <BrowserNarration
                  text={lessonNarrationText(lesson)}
                  title={lesson.title}
                />
              </div>
            </div>
          )}

          {/* Lesson Info (always shown) */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm mt-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                {lesson.duration_minutes && (
                  <p className="text-sm text-slate-500 flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {lesson.duration_minutes} minutes
                  </p>
                )}
              </div>
              {isCompleted && (
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Mark Complete Button */}
          <div className="mt-6">
            {isCompleted ? (
              <Button
                data-testid="mark-incomplete-btn"
                onClick={handleMarkIncomplete}
                disabled={markingComplete}
                variant="outline"
                className="w-full h-14 rounded-xl border-slate-200 text-slate-600 hover:bg-slate-50"
              >
                {markingComplete ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-600"></span>
                    Updating...
                  </span>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5 mr-2 text-emerald-500" />
                    Done — tap to undo
                  </>
                )}
              </Button>
            ) : (
              <Button
                data-testid="mark-complete-btn"
                onClick={handleMarkComplete}
                disabled={markingComplete}
                className="w-full h-14 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-xl shadow-md shadow-emerald-500/20"
              >
                {markingComplete ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                    Saving...
                  </span>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Mark this as done
                  </>
                )}
              </Button>
            )}
          </div>

          {/* Batch P1 · Lesson Truth-State — canonical async receipt.
              Renders idle when `truth.state` is null. Failed loads /
              mark actions expose Retry + Dismiss (never a dead end). */}
          <TruthReceipt
            state={truth.state}
            message={truth.message}
            onRetry={isCompleted ? handleMarkIncomplete : handleMarkComplete}
            onDismiss={() => truth.setState(null)}
          />

          {/* B0 Step 5 — single canonical post-lesson reflection
              surface. Shows only when the lesson is complete and the
              learner hasn't already reflected on it; replaces the
              retired PostLessonReflection modal + ConfidenceCheck
              standalone route. */}
          {isCompleted && !hasReflected && (
            <LessonEndReflection
              lessonId={lessonId}
              onCaptured={() => setHasReflected(true)}
            />
          )}

          {/* Navigation */}
          <div className="flex items-center gap-4 mt-6">
            {prevLesson ? (
              <Link to={`/lesson/${prevLesson.id}`} className="flex-1">
                <Button
                  data-testid="prev-lesson-btn"
                  variant="outline"
                  className="w-full h-12 rounded-xl border-slate-200"
                >
                  <ChevronLeft className="w-5 h-5 mr-1" />
                  <span className="truncate">Previous</span>
                </Button>
              </Link>
            ) : (
              <div className="flex-1" />
            )}
            {nextLesson ? (
              <Link to={`/lesson/${nextLesson.id}`} className="flex-1">
                <Button
                  data-testid="next-lesson-btn"
                  className="w-full h-12 bg-blue-700 hover:bg-blue-800 text-white font-bold rounded-xl"
                >
                  <span className="truncate">Next</span>
                  <ChevronRight className="w-5 h-5 ml-1" />
                </Button>
              </Link>
            ) : isLastInModule && module ? (
              // B0 Step 5 — Confidence Check standalone CTA retired.
              // The reflection rail above (LessonEndReflection) is now
              // the canonical post-lesson surface; module-end no longer
              // forks into a separate quiz route. Backend schema/
              // endpoints/tests for /confidence-check are preserved.
              <Link to="/dashboard" className="flex-1">
                <Button
                  data-testid="finish-module-btn"
                  className="w-full h-12 bg-blue-700 hover:bg-blue-800 text-white font-bold rounded-xl"
                >
                  <BookOpen className="w-5 h-5 mr-2" />
                  Back to Dashboard
                </Button>
              </Link>
            ) : (
              <Link to="/dashboard" className="flex-1">
                <Button
                  data-testid="finish-course-btn"
                  className="w-full h-12 bg-blue-700 hover:bg-blue-800 text-white font-bold rounded-xl"
                >
                  <BookOpen className="w-5 h-5 mr-2" />
                  Back to Dashboard
                </Button>
              </Link>
            )}
          </div>

          {/* Upgrade prompt after lesson content for free users */}
          {!isSubscribed && hasAccess && (
            <UpgradePrompt variant="inline" context="Enjoying this? Want more support and faster progress?" />
          )}
        </div>
        )}
      </div>

      {/* B0 Step 5 — PostLessonReflection modal retired. The
          canonical post-lesson reflection surface is the inline
          `LessonEndReflection` rail rendered above the navigation
          row when the lesson is complete. Modal component file
          preserved for historical data shape. */}
    </div>
  );
}

